import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import prepareTranslate from '@/helpers/dictionary';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronRight } from '@tabler/icons-react';
import agentMutations from '@/api/mutitations/agent';
import { IDENTIFIER_QUERIES, PROCESS_NOTIFICATION_SCREENS } from '@/helpers/enums';
import { useForm } from '@mantine/form';
import validations from '@/helpers/validation';
import {
  Modal,
  Text,
  ScrollArea,
  ActionIcon,
  LoadingOverlay,
  Center,
} from '@mantine/core';
import agentEditValidations from '@/helpers/validation/pages/agentEdit';
import { useModalQueue } from '@/hooks/FileUploadResponseContext';
import Actionmodal from '@/components/Modal/Index';
import s3Mutations from '@/api/mutitations/s3';
import processInitiatorMutations from '@/api/mutitations/processInitiator';
import DisplayAgent from './DisplayAgent';
import CreateAgentInProcess from './CreateAgentInProcess';
import styles from './style.module.css';
import CreateClientOrAgreement from './CreateClientOrAgreement';

function ProcessNotification(props) {
  const {
    identifiers,
    ruleDetails,
    onClose,
  } = props;

  const { showModal, closeModal } = useModalQueue();

  const {
    title: ruleTitle,
    id: ruleId,
  } = ruleDetails;

  const {
    origionYear: firstIdentifierYear, origionMonth: firstIdentifierMonth, referenceYear,
  } = identifiers[0];
  const { useUpload } = s3Mutations;

  const t = prepareTranslate();

  // eslint-disable-next-line max-len
  const { mutateAsync: createAgentMutate, isPending: createAgentPending } = agentMutations.useCreateAgent();
  const { mutateAsync: updateAgentMutate } = agentMutations.useUpdateAgent();
  // eslint-disable-next-line max-len
  const { mutateAsync: createAndDeleteProcessInitMutate } = processInitiatorMutations.useCreateAndDelete();
  const { mutateAsync: uploadMutate } = useUpload();

  const [opened, { close, open }] = useDisclosure(false);
  const [loading, { close: loadingOff, open: loadingOn }] = useDisclosure(false);

  // null or agentId
  const [createdAgent, setCreatedAgent] = useState(null);
  const [page, setPage] = useState(PROCESS_NOTIFICATION_SCREENS.dispalyAgent);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (page !== PROCESS_NOTIFICATION_SCREENS.done) {
      open();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const form = useForm({
    initialValues: {
      personalDetails: {
        children: [],
      },
      agreements: [
        {
          branchId: [],
        },
      ],
      clients: [{ client: '' }],
      alternativeFunds: [{ alternativeFund: '' }],
    },
    validate: {
      agreements: {
        agentNumber: (value) => validations.required(value),
        companyId: (value) => validations.required(value),
        branchId: (value) => validations.required(value),
        accordType: (value) => validations.required(value),
      },
      clients: {
        companyId: (_, values, path) => agentEditValidations.companyOrHp(values, path),
        hp: (_, values, path) => agentEditValidations.companyOrHp(values, path),
        accordType: (value) => validations.required(value),
        clientNumber: (value) => validations.required(value),
        branchId: (value) => validations.required(value),
      },
      alternativeFunds: {
        companyId: (value) => validations.required(value),
        accordType: (value) => validations.required(value),
        alternativeFundNumber: (value) => validations.required(value),
        branchId: (value) => validations.required(value),
      },
    },
  });

  const handleSubmit = async (agentValues) => {
    try {
      loadingOn();
      let uploadFiles;
      const formData = agentValues;
      const { agreements, clients, alternativeFunds } = formData;

      delete formData.personalDetails.createdAt;
      delete formData.personalDetails.updatedAt;
      delete formData.personalDetails.homeFullAddress;
      delete formData.personalDetails.officeFullAddress;
      delete formData.personalDetails.fullName;

      // // Remove the createdAt field from each item
      const sanitizedData = agreements.map((item) => {
        // eslint-disable-next-line no-param-reassign
        delete item.createdAt;
        // eslint-disable-next-line no-param-reassign
        delete item.updatedAt;
        // eslint-disable-next-line no-param-reassign
        delete item.datesArray;

        return item;
      });

      // // Remove the createdAt field from each item
      const clientsSanitizedData = clients.map((item) => {
        // eslint-disable-next-line no-param-reassign
        delete item.createdAt;
        // eslint-disable-next-line no-param-reassign
        delete item.updatedAt;
        return item;
      });

      // Remove the createdAt field from each item
      const alternativeFundsSanitizedData = alternativeFunds.map((item) => {
        // eslint-disable-next-line no-param-reassign
        delete item.createdAt;
        // eslint-disable-next-line no-param-reassign
        delete item.updatedAt;
        return item;
      });

      const isAgreement = clientOrAgreement === PROCESS_NOTIFICATION_SCREENS.createAgreement;
      if (isAgreement) {
        const agreementData = formData.agreements[formData.agreements.length - 1];
        const { files } = agreementData;

        if (files.length > 0) {
          const filesData = new FormData();

          files.forEach((file) => {
            const realFile = file.file;
            filesData.append('files', realFile, file.name);
          });

          try {
            uploadFiles = await uploadMutate(filesData);
            agreementData.files = uploadFiles.files;
          } catch {
            setError(true);
            return;
          }
        }
      }

      formData.agreements = sanitizedData;
      formData.clients = clientsSanitizedData;
      formData.alternativeFunds = alternativeFundsSanitizedData;

      const newAgent = await updateAgentMutate(formData);
      const nextIndex = currentIndex + 1;

      if (!newAgent) {
        loadingOff();
        setError(true);
      } else if (nextIndex <= (identifiers.length - 1)) {
        setPage(PROCESS_NOTIFICATION_SCREENS.dispalyAgent);
        setCurrentIndex(nextIndex);
        setCreatedAgent(null);
        setError(false);
      } else {
        setPage(PROCESS_NOTIFICATION_SCREENS.done);
        const processInitiatorObj = {
          year: firstIdentifierYear,
          month: firstIdentifierMonth,
          rule: ruleId,
          trigger: 'unknownAndentifiers',
          referenceYear,
        };
        try {
          await createAndDeleteProcessInitMutate(processInitiatorObj);
        } catch {
          onClose();
          close();
        }
        close();
        onClose();
        showModal({
          id: 'doneCreatingInProcess',
          component: <Actionmodal
            label={t('components.doneCreatingInProcess.title')}
            message={t('components.doneCreatingInProcess.subTitle')}
            opened={true}
            withCloseButton={true}
            onClose={closeModal} />,
        });
      }
    } catch {
      setError(true);
    }

    loadingOff();
  };

  useEffect(() => {
    setError(false);
  }, [page]);

  const disabled = createAgentPending;
  // After were done creating all the agents and accords, identifiers[currentIndex]
  // wull be undefined.
  const currentIdentifier = identifiers[currentIndex] || {};

  let clientOrAgreement;
  if (currentIdentifier.identifierQuery === IDENTIFIER_QUERIES.agreement) {
    clientOrAgreement = PROCESS_NOTIFICATION_SCREENS.createAgreement;
  } else if (currentIdentifier.identifierQuery === IDENTIFIER_QUERIES.client) {
    clientOrAgreement = PROCESS_NOTIFICATION_SCREENS.createClient;
  } else {
    clientOrAgreement = PROCESS_NOTIFICATION_SCREENS.createAlternativeFund;
  }

  let errorMessage;
  if (currentIdentifier.identifierQuery === IDENTIFIER_QUERIES.agreement) {
    errorMessage = t('components.processNotification.errors.agreement');
  } else if (currentIdentifier.identifierQuery === IDENTIFIER_QUERIES.client) {
    errorMessage = t('components.processNotification.errors.client');
  } else {
    errorMessage = t('components.processNotification.errors.alternativeFund');
  }

  const displayBackButtonTerm = (
    page !== PROCESS_NOTIFICATION_SCREENS.dispalyAgent
    && page !== PROCESS_NOTIFICATION_SCREENS.done
  );

  const displayAutoComplete = (
    page === PROCESS_NOTIFICATION_SCREENS.createClient
    || page === PROCESS_NOTIFICATION_SCREENS.createAgreement
    || page === PROCESS_NOTIFICATION_SCREENS.createAlternativeFund
  );

  return (
    <>
      {page !== PROCESS_NOTIFICATION_SCREENS.done && < Modal.Root
        opened={opened}
        onClose={close}
        scrollAreaComponent={ScrollArea.Autosize}
        size='80%'
        p='xl'
        zIndex={300}
        centered
        closeOnClickOutside={false}
      >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header className={styles.modal_header}>
          <div className={styles.header_content}>
            {displayBackButtonTerm && (
              <ActionIcon
                color='root'
                variant='transparent'
                onClick={() => setPage(0)}
                disabled={disabled}
                className={styles.back_button}
              >
                <IconChevronRight />
              </ActionIcon>
            )}
          </div>
        </Modal.Header>
        <Modal.Body>
          <LoadingOverlay visible={loading} />
          {
            page === PROCESS_NOTIFICATION_SCREENS.dispalyAgent
            && <DisplayAgent
              currentIdentifier={currentIdentifier}
              currentIdentifierSpot={currentIndex + 1}
              setPage={setPage}
              clientOrAgreement={clientOrAgreement}
              createdAgent={createdAgent}
              identifiers={identifiers}
              fileTitle={ruleTitle}
              onClose={onClose}
            />
          }
          {
            page === PROCESS_NOTIFICATION_SCREENS.createAgent
            && <CreateAgentInProcess
              setPage={setPage}
              createAgentMutate={createAgentMutate}
              clientOrAgreement={clientOrAgreement}
              setCreatedAgent={setCreatedAgent}
            />
          }
          <form onSubmit={form.onSubmit(handleSubmit)}>
            {
              displayAutoComplete && <CreateClientOrAgreement
                currentIdentifier={currentIdentifier}
                createdAgent={createdAgent}
                clientOrAgreement={clientOrAgreement}
                form={form}
              />
            }
          </form>
           <Center mt='lg'>
              {
                error && <Text c='red'>
                  {errorMessage}
                </Text>
              }
            </Center>
        </Modal.Body>
      </Modal.Content>
      </Modal.Root >
      }
    </>
  );
}

ProcessNotification.propTypes = {
  notificationId: PropTypes.string,
  identifiers: PropTypes.arrayOf(PropTypes.shape({
    company: PropTypes.string.isRequired,
    product: PropTypes.string.isRequired,
    identifierNumber: PropTypes.string.isRequired,
    year: PropTypes.number.isRequired,
    month: PropTypes.number.isRequired,
    origionYear: PropTypes.number.isRequired,
    origionMonth: PropTypes.number.isRequired,
    referenceYear: PropTypes.number,
    identifierQuery: PropTypes.string.isRequired,
  })).isRequired,
  ruleDetails: PropTypes.object,
  ruleFile: PropTypes.string.isRequired,
  onClose: PropTypes.func,
};

export default ProcessNotification;
